import React, {
  useMemo, useState, useCallback, useEffect, useRef,
} from 'react';
import {
  Center,
  Spinner,
  Heading,
  Text,
  Box,
  HStack,
  VStack,
  Pressable,
  Icon,
  useToken,
  Button,
  ButtonText,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useGetLecturerSlots } from '@/common/api/endpoints/appointments/appointments';
import { getGetUserMandatesQueryKey } from '@/common/api/endpoints/users/users';
import { useAuthStore } from '@/common/auth/store';
import { useTenantEventKinds } from '@/common/context/TenantSettingsStore';
import { useDialog } from '@/common/context/DialogContext';
import {
  X, ChevronLeft, ChevronRight, Calendar as CalendarIcon, List,
} from 'lucide-react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Calendar } from 'react-native-big-calendar';
import { Dimensions } from 'react-native';
import dayjs from 'dayjs';
import type { AppointmentSlot } from '@/common/api/models/appointmentSlot';
import SlotForm from './components/SlotForm';
import { useCalendarBadges } from './hooks/useCalendarBadges';
import PlanningGapsWarning from './components/PlanningGapsWarning';
import { buildSlotWindowParams } from './utils/slotWindowParams';

interface SidebarState {
  mode: 'create' | 'edit';
  date?: Date;
  viewMode?: 'week' | 'month';
  slot?: AppointmentSlot;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  slot: AppointmentSlot;
}

const GESTURE_HANDLER_STYLE = { flex: 1 };

/**
 * EventCalendar component for managing lecturer event tasks and schedules.
 * Renamed from SlotManager.
 */
const EventCalendar: React.FC = () => {
  const { t } = useTranslation();
  const { showDialog } = useDialog();
  const userId = useAuthStore((state) => state.user.id);
  const queryClient = useQueryClient();
  const prevSlotsRef = useRef<AppointmentSlot[]>([]);
  const lastSuccessfulWindowRef = useRef<{ data?: { data?: AppointmentSlot[] } } | undefined>(undefined);

  const getSlotKind = (slot: AppointmentSlot | undefined) => {
    if (!slot) return 'available';
    if (slot.status === 'planned') return 'planned';
    if (slot.status === 'completed') return 'completed';
    const cap = typeof slot.capacity === 'number' ? slot.capacity : 0;
    const avail = typeof slot.availableSpots === 'number' ? slot.availableSpots : 0;
    if (cap > 0 && avail <= 0) return 'full';
    return 'available';
  };

  // @ts-expect-error - textSubtle is a semantic token
  const textSubtle = useToken('colors', 'textSubtle');
  // @ts-expect-error - textHeading is a semantic token
  const textHeading = useToken('colors', 'textHeading');

  // Custom tokens for calendar specific states
  // @ts-expect-error - accent100 is a dynamic token
  const accent100 = useToken('colors', 'accent100');
  // @ts-expect-error - accent200 is a dynamic token
  const accent200 = useToken('colors', 'accent200');
  // @ts-expect-error - accent500 is a dynamic token
  const accent500 = useToken('colors', 'accent500');
  // @ts-expect-error - accent600 is a dynamic token
  const accent600 = useToken('colors', 'accent600');
  // @ts-expect-error - neutral100 is a dynamic token
  const neutral100 = useToken('colors', 'neutral100');
  // @ts-expect-error - neutral200 is a dynamic token
  const neutral200 = useToken('colors', 'neutral200');
  // @ts-expect-error - neutral400 is a dynamic token
  const neutral400 = useToken('colors', 'neutral400');

  const SLOT_VISUALS = useMemo(
    () => ({
      planned: {
        bg: accent100,
        border: accent500,
        borderWidth: 1,
        borderStyle: 'dashed' as const,
        text: textHeading,
      },
      available: {
        bg: accent200,
        border: accent600,
        borderWidth: 1,
        borderStyle: 'solid' as const,
        text: textHeading,
      },
      full: {
        bg: accent200,
        border: accent600,
        borderWidth: 1,
        borderStyle: 'solid' as const,
        text: textHeading,
      },
      completed: {
        bg: neutral100,
        border: neutral200,
        borderWidth: 1,
        borderStyle: 'solid' as const,
        text: textSubtle,
      },
    }),
    [accent100, accent200, accent500, accent600, neutral100, neutral200, textHeading, textSubtle],
  );

  const renderEvent = useCallback(
    (event: CalendarEvent, touchableOpacityProps: Record<string, unknown>) => {
      const { key: unusedKey, ...pressableProps } = touchableOpacityProps || {};
      const kind = getSlotKind(event.slot);
      const visuals = SLOT_VISUALS[kind as keyof typeof SLOT_VISUALS];
      const color = visuals?.text || textHeading;
      return (
        <Pressable
          key={event.id}
          {...pressableProps}
          testID={`lecturer-calendar-slot-chip-${event.id}`}
          justifyContent="flex-start"
          pt="$1"
        >
          <HStack alignItems="center" space="xs" flex={1}>
            <Box
              w={6}
              h={6}
              borderRadius="$full"
              backgroundColor={visuals?.border}
              flexShrink={0}
            />
            <Text size="xs" numberOfLines={1} flex={1} color={color}>
              {event.title}
            </Text>
          </HStack>
        </Pressable>
      );
    },
    [SLOT_VISUALS, textHeading],
  );

  const [cursorDate, setCursorDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('month');

  const slotWindowMonthKey = cursorDate.format('YYYY-MM');
  const slotQueryParams = useMemo(
    () => buildSlotWindowParams(dayjs(`${slotWindowMonthKey}-01`)),
    [slotWindowMonthKey],
  );

  const {
    data,
    isPending,
    isError,
    refetch,
    isFetching,
  } = useGetLecturerSlots(slotQueryParams, {
    query: {
      refetchInterval: 30000,
      staleTime: 10000,
      placeholderData: (previousData) => previousData,
    },
  });

  useEffect(() => {
    if (data && !isError) {
      lastSuccessfulWindowRef.current = data;
    }
  }, [data, isError]);

  const resolvedWindowData = data ?? lastSuccessfulWindowRef.current;
  const slots = useMemo(
    () => (resolvedWindowData?.data?.data || []) as AppointmentSlot[],
    [resolvedWindowData],
  );
  const hasCachedSlots = resolvedWindowData !== undefined;

  const [sidebar, setSidebar] = useState<SidebarState | null>(null);

  useEffect(() => {
    if (sidebar?.mode === 'edit' && sidebar.slot) {
      const updatedSlot = slots.find((s) => s.slotId === sidebar.slot?.slotId);
      if (updatedSlot && updatedSlot !== sidebar.slot) {
        setSidebar((prev) => (prev ? { ...prev, slot: updatedSlot } : null));
      }
    }
  }, [slots, sidebar?.mode, sidebar?.slot]);

  useEffect(() => {
    const prevSlots = prevSlotsRef.current;
    const currentSlots = slots;

    if (prevSlots.length === 0) {
      prevSlotsRef.current = currentSlots;
      return;
    }

    const hasConfirmationChange = currentSlots.some((slot) => {
      const prevSlot = prevSlots.find((p) => p.slotId === slot.slotId);
      return prevSlot?.status === 'planned' && slot.status !== 'planned';
    });

    if (hasConfirmationChange) {
      queryClient.invalidateQueries({
        queryKey: getGetUserMandatesQueryKey(userId),
      });
    }

    prevSlotsRef.current = currentSlots;
  }, [slots, userId, queryClient]);

  const {
    planningGaps, totalMissingSlots,
  } = useCalendarBadges();
  const { getEventKindById } = useTenantEventKinds();

  const screenHeight = Dimensions.get('window').height;
  const calendarHeight = screenHeight - 144;

  const gotoPrev = () => setCursorDate((d) => d.subtract(1, viewMode));
  const gotoNext = () => setCursorDate((d) => d.add(1, viewMode));
  const gotoNow = () => setCursorDate(dayjs());

  const handleRetryWindow = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const formatPeriodLabel = () => {
    if (viewMode === 'month') {
      return cursorDate.format('MMMM YYYY');
    }
    const startOfWeek = cursorDate.startOf('week');
    const endOfWeek = cursorDate.endOf('week');
    return `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D, YYYY')}`;
  };

  const calendarEvents = useMemo(() => slots
    .slice()
    .sort((a, b) => {
      const sa = new Date(a.startTime).getTime();
      const sb = new Date(b.startTime).getTime();
      if (sa !== sb) return sa - sb;
      return String(a.slotId).localeCompare(String(b.slotId));
    })
    .map((slot): CalendarEvent => {
      const startTimeLabel = dayjs(slot.startTime).format('HH:mm');
      const eventKind = getEventKindById(slot.eventKindId);
      const eventKindLabel = eventKind?.label || 'Event';
      const isPlanned = slot.status === 'planned';
      const isCompleted = slot.status === 'completed';

      let title = '';
      if (viewMode === 'month') {
        title = `${startTimeLabel} ${eventKindLabel}`;
        const monthSuffix = isPlanned ? ' (planned)' : isCompleted ? ' (completed)' : '';
        title = `${title}${monthSuffix}`;
      } else {
        title = eventKindLabel;
        if (slot.boundTasks && slot.boundTasks.length > 0) {
          title += `\n${slot.boundTasks.length} tasks`;
        }
      }

      return {
        id: slot.slotId,
        title,
        start: new Date(slot.startTime),
        end: new Date(slot.endTime),
        slot,
      };
    }), [slots, viewMode, getEventKindById]);

  const openCreateSidebar = useCallback(
    (date: Date) => {
      setSidebar({ mode: 'create', date, viewMode });
    },
    [viewMode],
  );

  const openEditSidebar = useCallback((slot: AppointmentSlot) => {
    setSidebar({ mode: 'edit', slot });
  }, []);

  const closeSidebar = () => setSidebar(null);

  const handlePressEvent = useCallback(
    (evt: CalendarEvent) => {
      if (evt?.slot) {
        openEditSidebar(evt.slot);
      }
    },
    [openEditSidebar],
  );

  const handlePressCell = useCallback(
    (date: Date) => {
      const clickedDateTime = dayjs(date);
      const minFutureTime = dayjs().add(1, 'hour');

      if (clickedDateTime.isBefore(minFutureTime)) {
        showDialog({
          title: t('lecturer.eventCalendar.error.pastSlot.title') || 'Cannot Create Slot',
          message: t('lecturer.eventCalendar.error.pastSlot.message'),
          buttons: [{ text: t('common.buttons.ok'), variant: 'primary' }],
        });
      } else {
        openCreateSidebar(date);
      }
    },
    [openCreateSidebar, showDialog, t],
  );

  if (isPending && !hasCachedSlots) {
    return (
      <Center flex={1} bg="$bgApp">
        <Spinner size="large" />
      </Center>
    );
  }

  if (isError && !hasCachedSlots) {
    return (
      <Center flex={1} bg="$bgApp">
        <Text color="$statusError">{t('common.messages.error')}</Text>
      </Center>
    );
  }

  const showWindowErrorStrip = isError && hasCachedSlots;

  return (
    <HStack flex={1} bg="$bgApp">
      <VStack flex={1}>
        {showWindowErrorStrip && (
          <HStack
            testID="lecturer-calendar-window-error"
            px="$4"
            py="$2"
            alignItems="center"
            justifyContent="space-between"
            bg="$statusErrorSubtle"
            borderBottomWidth={1}
            borderBottomColor="$statusError"
          >
            <Text flex={1} color="$statusError" size="sm">
              {t('lecturer.eventCalendar.windowLoadError.message')}
            </Text>
            <Button
              testID="lecturer-calendar-window-retry"
              size="sm"
              variant="outline"
              onPress={handleRetryWindow}
              isDisabled={isFetching}
            >
              <ButtonText>{t('lecturer.eventCalendar.windowLoadError.retry')}</ButtonText>
            </Button>
          </HStack>
        )}
        {/* Header */}
        <HStack px="$6" py="$3" alignItems="center" justifyContent="space-between" borderBottomWidth={1} borderBottomColor="$borderLight">
          <HStack alignItems="center" space="md">
            <HStack alignItems="center" space="xs">
              <Pressable testID="lecturer-calendar-prev" onPress={gotoPrev} p="$2" borderRadius="$full" $hover={{ bg: '$bgHover' }}>
                <Icon as={ChevronLeft} size="md" color="$textSubtle" />
              </Pressable>
              <Pressable testID="lecturer-calendar-next" onPress={gotoNext} p="$2" borderRadius="$full" $hover={{ bg: '$bgHover' }}>
                <Icon as={ChevronRight} size="md" color="$textSubtle" />
              </Pressable>
            </HStack>

            <Heading size="lg" color="$textHeading" fontWeight="semibold">
              {formatPeriodLabel()}
            </Heading>

            <Pressable onPress={gotoNow} px="$3" py="$1.5" borderRadius="$md" borderWidth={1} borderColor="$borderLight" $hover={{ bg: '$bgHover' }}>
              <Text size="sm" color="$textSubtle" fontWeight="medium">
                {t('lecturer.eventCalendar.goToNow') || 'Today'}
              </Text>
            </Pressable>
          </HStack>

          <HStack alignItems="center" space="lg">
            <PlanningGapsWarning
              planningGaps={planningGaps}
              totalMissingSlots={totalMissingSlots}
              getEventKindById={getEventKindById}
            />

            <HStack alignItems="center" space="md">
              <HStack alignItems="center" space="xs">
                <Box w={10} h={10} bg="$accent100" borderColor="$accent500" borderWidth={1} borderStyle="dashed" borderRadius={3} />
                <Text size="xs" color="$textSubtle">{t('lecturer.eventCalendar.legend.planned') || 'Planned'}</Text>
              </HStack>
              <HStack alignItems="center" space="xs">
                <Box w={10} h={10} bg="$accent200" borderColor="$accent600" borderWidth={1} borderRadius={3} />
                <Text size="xs" color="$textSubtle">{t('lecturer.eventCalendar.legend.available') || 'Available'}</Text>
              </HStack>
            </HStack>
          </HStack>

          <HStack alignItems="center" space="xs" bg="$bgHover" p="$1" borderRadius="$lg">
            <Pressable
              onPress={() => setViewMode('month')}
              px="$3"
              py="$1.5"
              borderRadius="$md"
              bg={viewMode === 'month' ? '$bgCard' : 'transparent'}
              softShadow={viewMode === 'month' ? '1' : undefined}
            >
              <HStack alignItems="center" space="xs">
                <Icon as={CalendarIcon} size="xs" color={viewMode === 'month' ? '$textHeading' : '$textSubtle'} />
                <Text size="sm" color={viewMode === 'month' ? '$textHeading' : '$textSubtle'} fontWeight={viewMode === 'month' ? 'semibold' : 'medium'}>
                  Month
                </Text>
              </HStack>
            </Pressable>
            <Pressable
              onPress={() => setViewMode('week')}
              px="$3"
              py="$1.5"
              borderRadius="$md"
              bg={viewMode === 'week' ? '$bgCard' : 'transparent'}
              softShadow={viewMode === 'week' ? '1' : undefined}
            >
              <HStack alignItems="center" space="xs">
                <Icon as={List} size="xs" color={viewMode === 'week' ? '$textHeading' : '$textSubtle'} />
                <Text size="sm" color={viewMode === 'week' ? '$textHeading' : '$textSubtle'} fontWeight={viewMode === 'week' ? 'semibold' : 'medium'}>
                  Week
                </Text>
              </HStack>
            </Pressable>
          </HStack>
        </HStack>

        {/* Calendar */}
        <Box flex={1} bg="$bgApp" px="$4">
          <Box flex={1}>
            <GestureHandlerRootView style={GESTURE_HANDLER_STYLE}>
              <Calendar
                events={calendarEvents}
                mode={viewMode}
                date={cursorDate.toDate()}
                height={calendarHeight}
                eventsAreSorted
                showTime={false}
                weekStartsOn={1}
                onPressEvent={handlePressEvent}
                onPressCell={handlePressCell}
                onPressMoreLabel={(_events: CalendarEvent[], date?: Date) => {
                  if (!date) return;
                  setCursorDate(dayjs(date));
                  setViewMode('week');
                }}
                maxVisibleEventCount={4}
                moreLabel={t('calendar.more')}
                locale={dayjs.locale()}
                calendarCellStyle={(date) => {
                  if (!date) return {};
                  const cellDate = dayjs(date);
                  const isToday = cellDate.isSame(dayjs(), 'day');
                  const isPast = cellDate.isBefore(dayjs(), 'day');
                  if (isToday) return { backgroundColor: neutral100 };
                  if (isPast) return { backgroundColor: neutral100, opacity: 0.7 };
                  return {};
                }}
                eventCellStyle={(event) => {
                  const kind = getSlotKind(event.slot);
                  const v = SLOT_VISUALS[kind as keyof typeof SLOT_VISUALS];
                  return {
                    backgroundColor: v.bg,
                    borderColor: v.border,
                    borderWidth: v.borderWidth,
                    borderStyle: v.borderStyle,
                    margin: 2,
                  };
                }}
                renderEvent={renderEvent}
                theme={{
                  typography: {
                    xs: { fontSize: 14, fontWeight: '600' },
                    sm: { fontSize: 15, fontWeight: '600' },
                    xl: { fontSize: 18, fontWeight: '600' },
                    moreLabel: { fontSize: 12, fontWeight: '600' },
                  },
                  palette: {
                    gray: { 500: neutral400 },
                    moreLabel: textSubtle,
                  },
                }}
              />
            </GestureHandlerRootView>
          </Box>
        </Box>
      </VStack>

      {/* Sidebar */}
      {!!sidebar && (
        <VStack w={320} bg="$bgCard" borderLeftWidth={1} borderLeftColor="$borderLight">
          <HStack px="$4" py="$3" justifyContent="space-between" alignItems="center" borderBottomWidth={1} borderBottomColor="$borderLight">
            <HStack alignItems="center" space="sm">
              <Text size="md" fontWeight="semibold" color="$textHeading">
                {sidebar.mode === 'create' ? t('lecturer.eventCalendar.sidebar.createTitle') : t('lecturer.eventCalendar.sidebar.editTitle')}
              </Text>
            </HStack>
            <Pressable onPress={closeSidebar} p="$1.5" borderRadius="$full" $hover={{ bg: '$bgHover' }}>
              <Icon as={X} size="sm" color="$textSubtle" />
            </Pressable>
          </HStack>
          <Box flex={1} p="$4">
            <SlotForm
              slot={sidebar.slot}
              date={sidebar.date}
              viewMode={sidebar.viewMode}
              onSuccess={closeSidebar}
              existingSlots={slots}
            />
          </Box>
        </VStack>
      )}
    </HStack>
  );
};

export default EventCalendar;
